import { Link } from 'react-router-dom'
import { DOCUMENT_CATEGORY_LABELS, type CrmNotification, type DocumentCategory } from '../lib/api'

const ESIGN_ALERT_LABELS: Record<string, string> = {
  esign_declined: 'BoldSign envelope declined',
  esign_expired: 'BoldSign envelope expired',
  esign_send_failed: 'BoldSign send failed',
}

/**
 * One row in the notification bell. Upload links are emailed to the
 * submitter and are not rendered here, even if a payload includes one.
 */
export function NotificationMessage({ notification }: { notification: CrmNotification }) {
  const data = notification.data
  const type = notification.type

  if (type === 'merchant_document_uploaded') {
    return (
      <p className="text-sm text-gray-700">
        Merchant uploaded{' '}
        <span className="font-medium">
          {DOCUMENT_CATEGORY_LABELS[String(data.category ?? '') as DocumentCategory] ?? String(data.category ?? '')}
        </span>
        {data.file_name ? (
          <span className="text-gray-500"> ({String(data.file_name)})</span>
        ) : null}
      </p>
    )
  }

  if (type === 'merchant_reporting_sync_failed') {
    return (
      <p className="text-sm text-gray-700">
        Reporting data unavailable for{' '}
        <span className="font-medium">{String(data.dba_name ?? 'merchant')}</span>
        {data.fiserv_mid ? (
          <span className="text-gray-500"> (MID {String(data.fiserv_mid)})</span>
        ) : null}
      </p>
    )
  }

  if (type === 'ticket_assigned' || type === 'ticket_sla_breach' || type === 'ticket_comment_posted') {
    const ticketNumber = String(data.ticket_number ?? 'Ticket')
    const subject = String(data.subject ?? 'Untitled ticket')
    const label = type === 'ticket_assigned'
      ? 'assigned'
      : type === 'ticket_sla_breach'
        ? (data.threshold === '4h' ? 'nearing SLA breach' : 'SLA breached')
        : 'comment posted'

    return (
      <Link to={String(data.url ?? `/tickets/${String(data.ticket_number ?? data.ticket_id ?? '')}`)} className="block text-sm text-gray-700 hover:text-blue-700">
        {ticketNumber}: {label}:{' '}
        <span className="font-medium">{subject}</span>
      </Link>
    )
  }

  if (type.startsWith('article_')) {
    return (
      <Link to={String(data.url ?? `/help/${String(data.slug ?? '')}`)} className="block text-sm text-gray-700 hover:text-blue-700">
        Help Hub:{' '}
        <span className="font-medium">{String(data.title ?? 'Article update')}</span>
      </Link>
    )
  }

  if (type === 'client_returned_delivery_failed') {
    const reason = data.reason === 'email_failed'
      ? 'The submitter email could not be sent.'
      : 'The original submitter is inactive or missing.'

    return (
      <Link to={String(data.url ?? `/leads/${String(data.lead_id ?? '')}`)} className="block text-sm text-gray-700 hover:text-blue-700">
        Client Returned alert was not delivered for{' '}
        <span className="font-medium">{String(data.dba_name ?? 'an application')}</span>
        <span className="text-gray-500"> — {reason} Open the lead to send a new upload link.</span>
      </Link>
    )
  }

  if (type === 'client_returned') {
    const comments = String(data.credit_officer_comments ?? '').trim()

    return (
      <Link to={String(data.url ?? `/leads/${String(data.lead_id ?? '')}`)} className="block text-sm text-gray-700 hover:text-blue-700">
        Fiserv returned{' '}
        <span className="font-medium">{String(data.dba_name ?? 'an application')}</span>
        {comments ? <span className="text-gray-500"> — {comments}</span> : null}
      </Link>
    )
  }

  if (type in ESIGN_ALERT_LABELS) {
    const detail = String(data.detail ?? '').trim()

    return (
      <Link to={String(data.url ?? (data.lead_id ? `/leads/${String(data.lead_id)}` : '/leads'))} className="block text-sm text-gray-700 hover:text-blue-700">
        {ESIGN_ALERT_LABELS[type]} for{' '}
        <span className="font-medium">{String(data.dba_name ?? 'an application')}</span>
        {detail ? <span className="text-gray-500"> — {detail}</span> : null}
      </Link>
    )
  }

  if (type === 'interchange_document_changed') {
    return (
      <Link to={String(data.url ?? '/settings/interchange-reviews')} className="block text-sm text-gray-700 hover:text-blue-700">
        <span className="font-medium">{String(data.title ?? 'Interchange document needs review')}</span>
        {data.body ? <span className="text-gray-500"> — {String(data.body)}</span> : null}
      </Link>
    )
  }

  return <p className="text-sm text-gray-700">{type}</p>
}
